import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import Loading from '../../components/Loading';
import axios from '../../services/axios';
import { searchCharges, exportCharges } from '../../services/finance/billingApi';
import { listBankAccounts } from '../../services/finance/bankAccountsApi';
import { financeErrorMessage } from '../Finance/utils/apiError';
import BankAccountMultiSelect from '../Finance/components/BankAccountMultiSelect';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../styles/layout/PageHeader';
import { CtaButton, GhostButton } from '../../styles/ui/Button';

import ChargeRow from './components/ChargeRow';
import {
   Card,
   CardHead,
   CardTitle,
   CardBody,
   CardFoot,
   Spacer,
   FilterGroup,
   GroupLabel,
   FieldLabel,
   PrimaryFieldLabel,
   TextInput,
   SelectInput,
   SearchFieldWrap,
   PrimaryGrid,
   AdvancedGrid,
   ClassificationGrid,
   VDivider,
   DateRow,
   DateGroupsRow,
   FieldErrorText,
   ActiveChip,
   TogglePill,
   LinkAction,
   MutedText,
   KpiPill,
   SortWrap,
   ListFoot,
   PageButton,
   EmptyBox,
} from './styles';

// ---------------------------------------------------------------------------
// Financeiro · Cobranças — listagem.
//
// Redesign de 2026-08-06 (design handoff em docs/design/design_handoff_cobrancas):
// filtros com hierarquia de 4 níveis (busca primária → vencimento → avançado →
// ações) e resultados em linha-cartão de 2 níveis, no lugar dos 8 campos soltos
// e da tabela de 10 colunas que quebrava texto.
//
// A camada de navegação segue o navegacao.md cap.1: filtros, ordenação, página e
// itens por página moram na URL, gravados com replace:true — voltar do detalhe
// restaura a pesquisa exata.
//
// Fora do redesign, por dependerem de backend inexistente (decisão 2026-08-06):
// - KPI "A receber": `meta` não expõe soma de pendentes; somar a página daria um
//   número errado com cara de total. Entra quando houver total_pending_amount.
// - Seleção em lote (Baixar PDFs / Enviar por e-mail): não há endpoint.
// ---------------------------------------------------------------------------

const PER_PAGE_OPTIONS = ['25', '50', '100'];
const DEFAULT_PER_PAGE = '25';

// Ordenação: rótulo do design → par (sort_by, sort_direction) do backend.
// ⚠️ Todas as chaves abaixo estão na whitelist de `sort_by` já usada na tela.
// Acrescentar opção nova exige confirmar o contrato: param desconhecido é
// ignorado em silêncio e vira ordenação fantasma (navegacao.md §1.10).
const SORT_OPTIONS = [
   { key: 'venc-asc', label: 'Vencimento ↑', by: 'due_date', dir: 'asc' },
   { key: 'venc-desc', label: 'Vencimento ↓', by: 'due_date', dir: 'desc' },
   {
      key: 'valor-desc',
      label: 'Maior valor',
      by: 'installment_value',
      dir: 'desc',
   },
   { key: 'id-desc', label: 'ID (mais recente)', by: 'id', dir: 'desc' },
   { key: 'cliente', label: 'Cliente A–Z', by: 'customer_name', dir: 'asc' },
];
const DEFAULT_SORT = 'venc-asc';

const STATUS_OPTIONS = [
   { value: '', label: 'Todos' },
   { value: 'PENDING', label: 'Pendente' },
   { value: 'RECEIVED', label: 'Recebido' },
   { value: 'OVERDUE', label: 'Vencido' },
   { value: 'CANCELLED', label: 'Cancelado' },
   { value: 'REFUNDED', label: 'Estornado' },
];

// Conjunto COMPLETO de propósito: o mock do design lista só quatro, mas enxugar
// aqui tiraria filtros que a operação usa hoje.
const COLLECTION_OPTIONS = [
   { value: '', label: 'Todas' },
   { value: 'ATIVA', label: 'Ativa' },
   { value: 'PAGA', label: 'Paga' },
   { value: 'CANCELADA', label: 'Cancelada' },
   { value: 'REVENDEDOR', label: 'Revendedor' },
   { value: 'RENEGOCIADA', label: 'Renegociada' },
   { value: 'CONSULTH', label: 'Consulth' },
   { value: 'JURIDICO', label: 'Jurídico' },
];

// Chaves do estado = query params = nomes do backend (snake_case).
const DEFAULT_FILTERS = {
   q: '',
   nfe_number: '',
   status: '',
   collection_status: '',
   origin_id: '',
   due_date_from: '',
   due_date_to: '',
   // Ranges do handoff 0106. "Emissão" = asaas_created_at (emissão real na
   // ASAAS); created_at não é oferecido pelo contrato.
   payment_date_from: '',
   payment_date_to: '',
   asaas_created_from: '',
   asaas_created_to: '',
};

// Os três ranges de data da linha de filtros — dirige o JSX e os chips.
const DATE_RANGES = [
   { label: 'Vencimento', fromKey: 'due_date_from', toKey: 'due_date_to' },
   {
      label: 'Recebimento',
      fromKey: 'payment_date_from',
      toKey: 'payment_date_to',
   },
   {
      label: 'Emissão',
      fromKey: 'asaas_created_from',
      toKey: 'asaas_created_to',
   },
];

function readFiltersFromUrl(searchParams) {
   const next = { ...DEFAULT_FILTERS };
   Object.keys(DEFAULT_FILTERS).forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null && value !== '') next[key] = value;
   });
   return next;
}

function readBankAccountIdsFromUrl(searchParams) {
   const raw = searchParams.get('bank_account_id');
   if (!raw) return [];
   return raw
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
}

// "YYYY-MM-DD" → "DD/MM/AAAA" por fatiamento (sem new Date(): o dia civil não
// pode deslocar com o fuso). Usado no chip do range de vencimento.
function isoToBr(iso) {
   return String(iso).split('-').reverse().join('/');
}

function formatCurrency(value) {
   return Number(value || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

// ─── Componente principal ─────────────────────────────────────
export default function BillingCharges() {
   const navigate = useNavigate();
   const permissions = useSelector(
      (state) => state.auth.user.permissions || []
   );
   const canCreate = permissions.includes('finance:charge:create');
   const canExport = permissions.includes('finance:report:export');
   const canReadBankAccounts = permissions.includes('finance:bank_account:read');

   const [searchParams, setSearchParams] = useSearchParams();
   const hasInitialQueryParams = searchParams.toString() !== '';

   // Rascunho (ligado aos controles) × aplicado (o que foi de fato pesquisado).
   // Ordenação, paginação e export usam SEMPRE o aplicado.
   const [filters, setFilters] = useState(() =>
      readFiltersFromUrl(searchParams)
   );
   const [appliedFilters, setAppliedFilters] = useState(filters);
   const [bankAccountIds, setBankAccountIds] = useState(() =>
      readBankAccountIdsFromUrl(searchParams)
   );
   const [appliedBankAccountIds, setAppliedBankAccountIds] =
      useState(bankAccountIds);

   // Erros de formato de data apontados pelo backend (422 do 0106, `field` por
   // param). FC4: acendem inline no DateRow certo, nunca toast. Só alcançável
   // por deep-link malformado — input type="date" já emite YYYY-MM-DD.
   const [dateFieldErrors, setDateFieldErrors] = useState({});

   const [sortKey, setSortKey] = useState(() => {
      const fromUrl = searchParams.get('sort');
      return SORT_OPTIONS.some((o) => o.key === fromUrl)
         ? fromUrl
         : DEFAULT_SORT;
   });
   const [perPage, setPerPage] = useState(() => {
      const fromUrl = searchParams.get('per_page');
      return PER_PAGE_OPTIONS.includes(fromUrl) ? fromUrl : DEFAULT_PER_PAGE;
   });
   const [currentPage, setCurrentPage] = useState(() => {
      const fromUrl = Number(searchParams.get('page'));
      return Number.isInteger(fromUrl) && fromUrl > 0 ? fromUrl : 1;
   });

   const [advancedOpen, setAdvancedOpen] = useState(true);

   const [charges, setCharges] = useState([]);
   const [isLoading, setIsLoading] = useState(false);
   const [hasSearched, setHasSearched] = useState(hasInitialQueryParams);
   const [totalPages, setTotalPages] = useState(1);
   const [totalCount, setTotalCount] = useState(0);
   const [totalAmount, setTotalAmount] = useState(0);
   const didInitialFetch = useRef(false);

   // ─── Catálogos dos filtros ────────────────────────────────
   const [billingOrigins, setBillingOrigins] = useState([]);
   const [bankAccounts, setBankAccounts] = useState([]);

   useEffect(() => {
      const fetchBillingOrigins = async () => {
         try {
            const response = await axios.get('/billing/origins');
            setBillingOrigins(response?.data?.data?.origins ?? []);
         } catch {
            // falha silenciosa — o select fica vazio mas não bloqueia a tela
         }
      };
      fetchBillingOrigins();
   }, []);

   useEffect(() => {
      if (!canReadBankAccounts) return;
      const fetchBankAccounts = async () => {
         try {
            // Sem filtro de status: conta inativa segue tendo histórico de
            // cobranças e precisa ser filtrável.
            setBankAccounts(await listBankAccounts({}));
         } catch {
            // idem: o filtro de empresa some, o resto da tela continua de pé.
         }
      };
      fetchBankAccounts();
   }, [canReadBankAccounts]);

   // ─── Busca ────────────────────────────────────────────────
   const fetchCharges = useCallback(
      async (values, accountIds, sortOptionKey, page, limit) => {
         const option =
            SORT_OPTIONS.find((o) => o.key === sortOptionKey) ??
            SORT_OPTIONS[0];
         setIsLoading(true);
         try {
            const result = await searchCharges(
               { ...values, bank_account_ids: accountIds },
               { key: option.by, direction: option.dir },
               page,
               Number(limit)
            );
            setCharges(result.charges);
            setTotalCount(result.total);
            setTotalPages(result.totalPages);
            setTotalAmount(result.totalAmount);
            setCurrentPage(page);
            setHasSearched(true);
            setDateFieldErrors((prev) =>
               Object.keys(prev).length ? {} : prev
            );
         } catch (err) {
            // 422 de formato de data (0106) chega com `field` por param —
            // FC4 manda inline no campo, nunca toast.
            if (
               err?.kind === 'validation' &&
               Object.keys(err.fieldErrors ?? {}).length
            ) {
               setDateFieldErrors(err.fieldErrors);
            } else {
               toast.error(
                  financeErrorMessage(err, 'Erro ao buscar boletos.')
               );
            }
         } finally {
            setIsLoading(false);
         }
      },
      []
   );

   // URL limpa = abertura fria → não busca. Com params (deep-link, F5, voltar
   // do detalhe) → restaura e busca.
   useEffect(() => {
      if (didInitialFetch.current) return;
      didInitialFetch.current = true;
      if (!hasInitialQueryParams) return;
      fetchCharges(
         appliedFilters,
         appliedBankAccountIds,
         sortKey,
         currentPage,
         perPage
      );
   }, [
      fetchCharges,
      hasInitialQueryParams,
      appliedFilters,
      appliedBankAccountIds,
      sortKey,
      currentPage,
      perPage,
   ]);

   const commitToUrl = useCallback(
      (values, accountIds, sortOptionKey, page, limit) => {
         // `applied=1` marca "houve busca" mesmo sem filtro nenhum — sem ele,
         // pesquisar tudo deixaria a URL limpa e voltar do detalhe esvaziaria a
         // lista (navegacao.md §1.5).
         const params = { applied: '1' };
         Object.keys(DEFAULT_FILTERS).forEach((key) => {
            if (values[key]) params[key] = values[key];
         });
         if (accountIds.length) params.bank_account_id = accountIds.join(',');
         if (sortOptionKey !== DEFAULT_SORT) params.sort = sortOptionKey;
         if (limit !== DEFAULT_PER_PAGE) params.per_page = limit;
         if (page > 1) params.page = String(page);
         setSearchParams(params, { replace: true });
      },
      [setSearchParams]
   );

   const runSearch = (values, accountIds, sortOptionKey, page, limit) => {
      setAppliedFilters(values);
      setAppliedBankAccountIds(accountIds);
      setSortKey(sortOptionKey);
      setPerPage(limit);
      commitToUrl(values, accountIds, sortOptionKey, page, limit);
      fetchCharges(values, accountIds, sortOptionKey, page, limit);
   };

   const updateField = (key, value) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      // Editar o campo apaga o erro de formato que o backend apontou nele.
      setDateFieldErrors((prev) => {
         if (!prev[key]) return prev;
         const next = { ...prev };
         delete next[key];
         return next;
      });
   };

   const handleSearch = (e) => {
      e.preventDefault();
      runSearch(filters, bankAccountIds, sortKey, 1, perPage);
   };

   const handleSortChange = (value) => {
      runSearch(appliedFilters, appliedBankAccountIds, value, 1, perPage);
   };

   const handlePerPageChange = (value) => {
      runSearch(appliedFilters, appliedBankAccountIds, sortKey, 1, value);
   };

   const handlePageChange = (page) => {
      if (page < 1 || page > totalPages || page === currentPage) return;
      runSearch(
         appliedFilters,
         appliedBankAccountIds,
         sortKey,
         page,
         perPage
      );
   };

   const handleClearFilters = () => {
      setFilters(DEFAULT_FILTERS);
      setAppliedFilters(DEFAULT_FILTERS);
      setBankAccountIds([]);
      setAppliedBankAccountIds([]);
      setDateFieldErrors({});
      setSortKey(DEFAULT_SORT);
      setPerPage(DEFAULT_PER_PAGE);
      setCharges([]);
      setTotalCount(0);
      setTotalPages(1);
      setTotalAmount(0);
      setCurrentPage(1);
      setHasSearched(false);
      setSearchParams({}, { replace: true });
   };

   // ─── Chips de filtro ativo ────────────────────────────────
   // Refletem o RASCUNHO: são o resumo do que será pesquisado, e removê-los
   // edita o formulário (a busca só roda no Pesquisar).
   const activeChips = useMemo(() => {
      const chips = [];

      DATE_RANGES.forEach(({ label: rangeLabel, fromKey, toKey }) => {
         const from = filters[fromKey];
         const to = filters[toKey];
         if (!from && !to) return;
         let label;
         if (from && to) {
            label = `${rangeLabel}: ${isoToBr(from)} → ${isoToBr(to)}`;
         } else if (from) {
            label = `${rangeLabel}: a partir de ${isoToBr(from)}`;
         } else {
            label = `${rangeLabel}: até ${isoToBr(to)}`;
         }
         chips.push({
            key: fromKey,
            label,
            clear: () =>
               setFilters((prev) => ({
                  ...prev,
                  [fromKey]: '',
                  [toKey]: '',
               })),
         });
      });
      if (filters.status) {
         chips.push({
            key: 'status',
            label: `Status: ${STATUS_OPTIONS.find((o) => o.value === filters.status)?.label ?? filters.status}`,
            clear: () => updateField('status', ''),
         });
      }
      if (filters.collection_status) {
         chips.push({
            key: 'collection',
            label: `Situação: ${COLLECTION_OPTIONS.find((o) => o.value === filters.collection_status)?.label ?? filters.collection_status}`,
            clear: () => updateField('collection_status', ''),
         });
      }
      if (filters.origin_id) {
         const origin = billingOrigins.find(
            (o) => String(o.id) === String(filters.origin_id)
         );
         chips.push({
            key: 'origin',
            label: `Origem: ${origin?.name ?? filters.origin_id}`,
            clear: () => updateField('origin_id', ''),
         });
      }
      bankAccountIds.forEach((id) => {
         const account = bankAccounts.find((a) => String(a.id) === id);
         chips.push({
            key: `bank-${id}`,
            label: account?.account_name ?? `Empresa ${id}`,
            clear: () =>
               setBankAccountIds((prev) => prev.filter((item) => item !== id)),
         });
      });

      return chips;
   }, [filters, billingOrigins, bankAccountIds, bankAccounts]);

   // ─── Export ───────────────────────────────────────────────
   const handleExport = async () => {
      setIsLoading(true);
      try {
         // Exporta o MESMO recorte que está na tela (filtros aplicados, não o
         // rascunho) — inclusive Nº da nota, que antes ficava de fora e fazia a
         // planilha divergir da listagem.
         const blob = await exportCharges({
            ...appliedFilters,
            bank_account_ids: appliedBankAccountIds,
         });
         const url = window.URL.createObjectURL(new Blob([blob]));
         const link = document.createElement('a');
         link.href = url;
         link.setAttribute('download', `boletos_${Date.now()}.xlsx`);
         document.body.appendChild(link);
         link.click();
         link.remove();
         window.URL.revokeObjectURL(url);
      } catch {
         toast.error('Erro ao exportar boletos.');
      } finally {
         setIsLoading(false);
      }
   };

   const rangeStart = charges.length ? (currentPage - 1) * Number(perPage) + 1 : 0;
   const rangeEnd = rangeStart ? rangeStart + charges.length - 1 : 0;

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Cobranças</PageTitle>
               <PageSubtitle>
                  Gerencie boletos e parcelamentos. Clique na linha para abrir o
                  detalhe.
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               {canCreate && (
                  // CTA escuro: abrir a tela de emissão é navegação. O azul
                  // fica reservado para o "Criar Boleto" que grava (FC7 G.1).
                  <CtaButton
                     type="button"
                     onClick={() => navigate('/billing/new')}
                     disabled={isLoading}
                  >
                     + Novo boleto
                  </CtaButton>
               )}
            </PageHeaderActions>
         </PageHeader>

         {/* ═══ Seção 1 — Filtros ═══════════════════════════════ */}
         <Card as="form" onSubmit={handleSearch} style={{ marginBottom: 16 }}>
            <CardHead>
               <CardTitle>
                  <span aria-hidden="true">⌕</span> Pesquisa
               </CardTitle>

               {activeChips.map((chip) => (
                  <ActiveChip key={chip.key}>
                     {chip.label}
                     <button
                        type="button"
                        onClick={chip.clear}
                        aria-label={`Remover filtro ${chip.label}`}
                     >
                        ×
                     </button>
                  </ActiveChip>
               ))}

               <Spacer />
               <TogglePill
                  type="button"
                  onClick={() => setAdvancedOpen((prev) => !prev)}
                  aria-expanded={advancedOpen}
               >
                  {advancedOpen ? 'Menos filtros' : 'Mais filtros'}
                  <span aria-hidden="true">{advancedOpen ? '▴' : '▾'}</span>
               </TogglePill>
            </CardHead>

            <CardBody>
               {/* 1.2 — busca primária */}
               <PrimaryGrid>
                  <div>
                     <PrimaryFieldLabel htmlFor="filter-q">
                        Cliente ou nº do pedido
                     </PrimaryFieldLabel>
                     <SearchFieldWrap>
                        <span className="icon" aria-hidden="true">
                           ⌕
                        </span>
                        <TextInput
                           id="filter-q"
                           type="text"
                           $withIcon
                           value={filters.q}
                           onChange={(e) => updateField('q', e.target.value)}
                           placeholder="Ex.: João Silva ou 9999"
                        />
                     </SearchFieldWrap>
                  </div>

                  <div>
                     <PrimaryFieldLabel htmlFor="filter-nfe">
                        Nº nota fiscal
                     </PrimaryFieldLabel>
                     <TextInput
                        id="filter-nfe"
                        type="text"
                        value={filters.nfe_number}
                        onChange={(e) =>
                           updateField('nfe_number', e.target.value)
                        }
                        placeholder="Ex.: 1234"
                     />
                  </div>
               </PrimaryGrid>

               {/* 1.3 — ranges de data: Vencimento, Recebimento e Emissão
                   (handoff 0106; presets de vencimento saíram em 2026-09-02).
                   "Emissão" filtra asaas_created_at — a emissão real na ASAAS;
                   created_at (insert no MWA) não é oferecido pelo contrato. */}
               <DateGroupsRow style={{ marginTop: 14 }}>
                  {DATE_RANGES.map(({ label, fromKey, toKey }) => (
                     <FilterGroup key={fromKey}>
                        <GroupLabel as="div">{label}</GroupLabel>
                        <DateRow>
                           <div>
                              <FieldLabel htmlFor={`filter-${fromKey}`}>
                                 De
                              </FieldLabel>
                              <TextInput
                                 id={`filter-${fromKey}`}
                                 type="date"
                                 value={filters[fromKey]}
                                 aria-invalid={Boolean(
                                    dateFieldErrors[fromKey]
                                 )}
                                 onChange={(e) =>
                                    updateField(fromKey, e.target.value)
                                 }
                              />
                              {dateFieldErrors[fromKey] && (
                                 <FieldErrorText role="alert">
                                    {dateFieldErrors[fromKey]}
                                 </FieldErrorText>
                              )}
                           </div>
                           <div>
                              <FieldLabel htmlFor={`filter-${toKey}`}>
                                 Até
                              </FieldLabel>
                              <TextInput
                                 id={`filter-${toKey}`}
                                 type="date"
                                 value={filters[toKey]}
                                 aria-invalid={Boolean(dateFieldErrors[toKey])}
                                 onChange={(e) =>
                                    updateField(toKey, e.target.value)
                                 }
                              />
                              {dateFieldErrors[toKey] && (
                                 <FieldErrorText role="alert">
                                    {dateFieldErrors[toKey]}
                                 </FieldErrorText>
                              )}
                           </div>
                        </DateRow>
                     </FilterGroup>
                  ))}
               </DateGroupsRow>

               {/* 1.4 — bloco avançado */}
               {advancedOpen && (
                  <AdvancedGrid>
                     <FilterGroup>
                        <GroupLabel as="div">Classificação</GroupLabel>
                        <ClassificationGrid>
                           <div>
                              <FieldLabel htmlFor="filter-status">
                                 Status do boleto
                              </FieldLabel>
                              <SelectInput
                                 id="filter-status"
                                 value={filters.status}
                                 onChange={(e) =>
                                    updateField('status', e.target.value)
                                 }
                              >
                                 {STATUS_OPTIONS.map((option) => (
                                    <option
                                       key={option.value}
                                       value={option.value}
                                    >
                                       {option.label}
                                    </option>
                                 ))}
                              </SelectInput>
                           </div>

                           <div>
                              <FieldLabel htmlFor="filter-collection">
                                 Situação
                              </FieldLabel>
                              <SelectInput
                                 id="filter-collection"
                                 value={filters.collection_status}
                                 onChange={(e) =>
                                    updateField(
                                       'collection_status',
                                       e.target.value
                                    )
                                 }
                              >
                                 {COLLECTION_OPTIONS.map((option) => (
                                    <option
                                       key={option.value}
                                       value={option.value}
                                    >
                                       {option.label}
                                    </option>
                                 ))}
                              </SelectInput>
                           </div>

                           <div>
                              <FieldLabel htmlFor="filter-origin">
                                 Origem
                              </FieldLabel>
                              <SelectInput
                                 id="filter-origin"
                                 value={filters.origin_id}
                                 onChange={(e) =>
                                    updateField('origin_id', e.target.value)
                                 }
                              >
                                 <option value="">Todas</option>
                                 {billingOrigins.map((origin) => (
                                    <option key={origin.id} value={origin.id}>
                                       {origin.name}
                                    </option>
                                 ))}
                              </SelectInput>
                           </div>
                        </ClassificationGrid>
                     </FilterGroup>

                     {canReadBankAccounts && (
                        <FilterGroup>
                           <GroupLabel as="div">
                              Empresa emissora
                              <span className="hint">
                                 {bankAccountIds.length
                                    ? `${bankAccountIds.length} selecionada${bankAccountIds.length > 1 ? 's' : ''}`
                                    : 'todas'}
                              </span>
                           </GroupLabel>
                           <BankAccountMultiSelect
                              accounts={bankAccounts}
                              selectedIds={bankAccountIds}
                              onChange={setBankAccountIds}
                              disabled={isLoading || !bankAccounts.length}
                           />
                        </FilterGroup>
                     )}
                  </AdvancedGrid>
               )}
            </CardBody>

            {/* 1.5 — ações do card */}
            <CardFoot>
               <LinkAction
                  type="button"
                  onClick={handleClearFilters}
                  disabled={isLoading}
               >
                  Limpar filtros
               </LinkAction>
               <Spacer />
               {hasSearched && (
                  <MutedText>
                     {totalCount} {totalCount === 1 ? 'título' : 'títulos'}
                  </MutedText>
               )}
               {canExport && (
                  <GhostButton
                     type="button"
                     onClick={handleExport}
                     disabled={isLoading || !hasSearched}
                     title={
                        hasSearched
                           ? undefined
                           : 'Pesquise antes de exportar — o arquivo sai com o mesmo recorte da tela'
                     }
                  >
                     Exportar
                  </GhostButton>
               )}
               {/* CTA escuro: pesquisar é consultivo, não escreve no banco
                   (FC7 G.1) — e é o mesmo primitivo já usado no "Pesquisar" das
                   demais listagens (ClienteList, BankAccountList). */}
               <CtaButton type="submit" disabled={isLoading}>
                  Pesquisar
               </CtaButton>
            </CardFoot>
         </Card>

         {/* ═══ Seção 2 — Resultados ════════════════════════════ */}
         <Card>
            <CardHead>
               <CardTitle>Resultados</CardTitle>
               <KpiPill>
                  Títulos <strong>{totalCount}</strong>
               </KpiPill>
               <KpiPill>
                  Valor total <strong>{formatCurrency(totalAmount)}</strong>
               </KpiPill>

               <Spacer />
               <SortWrap>
                  <label htmlFor="sort-select">Ordenar</label>
                  <select
                     id="sort-select"
                     value={sortKey}
                     onChange={(e) => handleSortChange(e.target.value)}
                     disabled={isLoading || !hasSearched}
                  >
                     {SORT_OPTIONS.map((option) => (
                        <option key={option.key} value={option.key}>
                           {option.label}
                        </option>
                     ))}
                  </select>
               </SortWrap>
            </CardHead>

            {charges.length ? (
               <>
                  <div>
                     {charges.map((charge) => (
                        <ChargeRow
                           key={String(charge.id)}
                           charge={charge}
                           formatCurrency={formatCurrency}
                           onOpen={() => navigate(`/billing/${charge.id}`)}
                        />
                     ))}
                  </div>

                  <ListFoot>
                     <MutedText>
                        Mostrando {rangeStart}–{rangeEnd} de {totalCount} ·
                        página {currentPage} de {totalPages || 1}
                     </MutedText>

                     <Spacer />

                     <PageButton
                        type="button"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={isLoading || currentPage <= 1}
                     >
                        Anterior
                     </PageButton>
                     <PageButton type="button" $current disabled>
                        {currentPage}
                     </PageButton>
                     <PageButton
                        type="button"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={isLoading || currentPage >= totalPages}
                     >
                        Próximo
                     </PageButton>

                     <VDivider style={{ height: 24 }} aria-hidden="true" />

                     <SortWrap>
                        <label htmlFor="per-page">Itens por página</label>
                        <select
                           id="per-page"
                           value={perPage}
                           onChange={(e) => handlePerPageChange(e.target.value)}
                           disabled={isLoading}
                        >
                           {PER_PAGE_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                 {option}
                              </option>
                           ))}
                        </select>
                     </SortWrap>
                  </ListFoot>
               </>
            ) : (
               <EmptyBox>
                  <div className="title">
                     {hasSearched
                        ? 'Nenhum boleto encontrado'
                        : 'Nenhuma pesquisa feita'}
                  </div>
                  <div className="desc">
                     {hasSearched
                        ? 'Ajuste os filtros e clique em Pesquisar.'
                        : 'Defina os filtros acima e clique em Pesquisar para listar os títulos.'}
                  </div>
               </EmptyBox>
            )}
         </Card>
      </Container>
   );
}
